import { useFormik } from "formik";
import * as yup from "yup";
import axios from "axios";
import { requireIdToken } from "../../../utils/firebaseAuth";
import { useModal } from "../../../context/ModalContext";
import { useRouter } from "next/router";
import { toast } from "react-hot-toast";
import { useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Input, TextArea } from "@jammable/ui-components/Forms";
import { Button, Checkbox, SecondaryButton } from "@jammable/ui-core";
import { Spinner } from "@jammable/ui-components/Loader";
import UploadImage from "../../UploadImage";
// ...

const EditCollectionModal = ({ collection, after, reroute }) => {
  const validationSchema = yup.object().shape({
    title: yup
      .string()
      .required("The model name is required")
      .max(40, "40 characters maximum."),
    description: yup.string().max(200, "200 characters maximum."),
  });
  const { closeModal } = useModal();
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [localImage, setLocalImage] = useState(null);
  const imageUploadRef = useRef(null);

  const formik = useFormik({
    initialValues: {
      title: collection?.title,
      description: collection?.description,
      isPrivate: collection?.isPrivate,
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      const token = await requireIdToken();

      let imageUrl;

      if (localImage) {
        const file = await imageUploadRef.current.getCroppedImageFile();
        const response = await axios.get("/api/s3/getPresignedUrl", {
          params: { type: "collection" },
          headers: { Authorization: token },
        });
        const { url, absoluteUrl } = response.data;

        const options = { headers: { "Content-Type": file.type } };

        await axios.put(url, file, options);
        imageUrl = absoluteUrl;
      }

      try {
        axios
          .post(
            "/api/collection/updateCollection",
            { ...values, collectionId: collection._id, image: imageUrl },
            { headers: { Authorization: token } },
          )
          .then(() => {
            if (after) {
              after();
            }
            closeModal("edit-model-modal");
            if (reroute) {
              router.push(`/collection/${collection?._id}`);
            }
            toast.success("Collection updated!");
          })
          .catch((err) => {
            setError(err.response.data.message);
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (error) {
        console.error(error);
      }
    },
  });

  return (
    <div className="vertical-flex-10">
      <div className="tw-text-2xl tw-font-black">Edit Collection</div>
      <form
        onChange={() => {
          setError(null);
        }}
        className="vertical-flex-15"
        onSubmit={formik.handleSubmit}
      >
        <UploadImage
          image={collection?.image?.url}
          setImage={setLocalImage}
          ref={imageUploadRef}
        />
        {error && <div className="error-message">{error}</div>}
        <div className="tw-flex tw-flex-col tw-gap-1">
          <Input
            fullWidth
            placeholder="Model Name..."
            label="Voice Name"
            name="title"
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.title && !!formik.errors.title}
            bordered
            css={{
              width: "100%",
              label: { color: "black", fontWeight: "bold", borderRadius: 8 },
              borderRadius: 8,
            }}
            radius={8}
          />
          {formik.touched.title && formik.errors.title && (
            <div className="error-text">{formik.errors.title}</div>
          )}
          <TextArea
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            name="description"
            error={formik.touched.description && !!formik.errors.description}
            aria-label="Describe your model!"
            label="Collection Description"
            css={{
              label: { color: "black", fontWeight: "bold", borderRadius: 8 },
              width: "100%",
              marginTop: 10,
            }}
            placeholder="(Optional) Describe your collection..."
            bordered
          />
          {formik.touched.description && formik.errors.description && (
            <div className="error-text">{formik.errors.description}</div>
          )}
        </div>
        <div className="tw-flex tw-items-center tw-justify-start tw-w-full tw-gap-2 tw-cursor-pointer tw-bg-white  tw-rounded-2xl">
          <Checkbox checked={formik.values.isPrivate} />
          <div
            onClick={() => {
              formik.setFieldValue("isPrivate", !formik.values.isPrivate);
            }}
          >
            <div className="tw-text-sm tw-font-semibold">
              Make Collection Private
            </div>
            <div className="tw-text-xs tw-text-black/40">
              Make your collection private to only you!
            </div>
          </div>
        </div>

        <Button
          disabled={loading}
          shadow
          type="submit"
          className="tw-rounded-full"
        >
          {loading ? (
            <Spinner size={4} className="tw-border-white tw-border-3" />
          ) : (
            "Save"
          )}
        </Button>
      </form>
    </div>
  );
};

export default EditCollectionModal;
